import { types, Instance, flow, cast } from 'mobx-state-tree'
import { BadgeApi } from '../../services/api-objects/BadgeApi'
import { Awaited } from '../../utility-types'
import { BadgeReadModel, BadgeUserReadModel, BadgeCreateModelType } from '../../models/base/badge'
import { createFileFromBlob } from '../../utils'

export const BadgesStoreModel = types
  .model('BadgesStore')
  .props({
    status: types.optional(types.enumeration(['idle', 'pending', 'done', 'error']), 'idle'),
    allBadges: types.maybe(types.array(BadgeReadModel)),
    customBadges: types.maybe(types.array(BadgeReadModel)),
    defaultBadges: types.maybe(types.array(BadgeReadModel)),
    badgeUsers: types.array(BadgeUserReadModel),
    triggerRefresh: types.frozen(),
  })
  .actions((self) => ({
    setStatus(value?: 'idle' | 'pending' | 'done' | 'error') {
      self.status = value || 'idle'
    },
    setAllBadges(data: typeof self.allBadges) {
      self.allBadges = data
    },
    setCustomBadges(data: typeof self.customBadges) {
      self.customBadges = data
    },
    setDefaultBadges(data: typeof self.defaultBadges) {
      self.defaultBadges = data
    },
    setBadgeUsers(data: typeof self.badgeUsers) {
      self.badgeUsers = data
    },
    setTriggerRefresh(status: any) {
      self.triggerRefresh = status
    },
  }))
  .views((self) => ({
    get isLoading() {
      return self.status === 'pending'
    },
  }))
  .actions((self) => ({
    createBadge: flow(function* (payload: BadgeCreateModelType) {
      self.setStatus('pending')
      try {
        if (payload.icon_url) {
          const file = yield createFileFromBlob(payload.icon_url, `badge_${payload.label}`)
          const result: Awaited<ReturnType<typeof BadgeApi.createBadge>> =
            yield BadgeApi.createBadge({ ...payload, file })
          if (result?.ok) {
            self.setTriggerRefresh(true)
            self.setStatus('done')
          } else {
            self.setStatus('error')
          }
        }
      } catch {
        self.setStatus('error')
      }
    }),
    editBadge: flow(function* (badge_id: number, payload: BadgeCreateModelType) {
      self.setStatus('pending')
      try {
        if (payload.icon_url) {
          let file
          if (payload.icon_url.includes('blob:')) {
            file = yield createFileFromBlob(payload.icon_url, `badge_${payload.label}`)
          }
          const result: Awaited<ReturnType<typeof BadgeApi.editBadge>> = yield BadgeApi.editBadge(
            badge_id,
            { ...payload, file },
          )
          if (result.ok) {
            self.setStatus('done')
          } else {
            self.setStatus('error')
          }
        }
      } catch {
        self.setStatus('error')
      }
    }),
    deleteBadge: flow(function* (badge_id: number) {
      self.setStatus('pending')
      try {
        const result: Awaited<ReturnType<typeof BadgeApi.deleteBadge>> =
          yield BadgeApi.deleteBadge(badge_id)
        if (result.ok) {
          self.setStatus('done')
        } else {
          self.setStatus('error')
        }
      } catch {
        self.setStatus('error')
      }
    }),
    toggleBadgeDisable: flow(function* (badge_id: number) {
      self.setStatus('pending')
      try {
        const result: Awaited<ReturnType<typeof BadgeApi.toggleBadgeDisable>> =
          yield BadgeApi.toggleBadgeDisable(badge_id)
        if (result.ok) {
          self.setStatus('done')
        } else {
          self.setStatus('error')
        }
      } catch {
        self.setStatus('error')
      }
    }),
    awardBadge: flow(function* (payload: {
      badge_id: number
      user_ids: number[]
      group_ids: number[]
    }) {
      self.setStatus('pending')
      try {
        const result: Awaited<ReturnType<typeof BadgeApi.awardBadge>> =
          yield BadgeApi.awardBadge(payload)
        if (result.ok) {
          self.setStatus('done')
        } else {
          self.setStatus('error')
        }
      } catch {
        self.setStatus('error')
      }
    }),
    revokeBadgeFromUser: flow(function* (payload: { badge_id: number; user_id: number }) {
      self.setStatus('pending')
      try {
        const result: Awaited<ReturnType<typeof BadgeApi.revokeBadge>> =
          yield BadgeApi.revokeBadge(payload)
        if (result.ok) {
          ;(self as Instance<typeof BadgesStoreModel>).getBadgeUsers(payload.badge_id)
          self.setTriggerRefresh(true)
          self.setStatus('done')
        } else {
          self.setStatus('error')
        }
      } catch {
        self.setStatus('error')
      }
    }),
    getBadgeUsers: flow(function* (badge_id: number) {
      self.setStatus('pending')
      try {
        const result: Awaited<ReturnType<typeof BadgeApi.badgeUsers>> =
          yield BadgeApi.badgeUsers(badge_id)
        if (result.ok && result.data) {
          self.setBadgeUsers(cast(result.data.users))
          self.setStatus('done')
        } else {
          self.setStatus('error')
        }
      } catch {
        self.setStatus('error')
      }
    }),
  }))

type BadgesStoreType = Instance<typeof BadgeReadModel>
export interface BadgesStore extends BadgesStoreType {}
